import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
}

interface ArgoStatus {
  syncStatus?: string;
  healthStatus?: string;
  revision?: string;
  imageTag?: string;
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      verticalAlign: 'middle',
      padding: theme.spacing(1.5, 2),
    },
  },
  serviceLink: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    '&:hover': { textDecoration: 'underline', color: theme.palette.primary.main },
  },
  meta: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  statusBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  chip: {
    fontSize: '0.65rem',
    height: 20,
    color: '#fff',
    fontWeight: 600,
  },
  revision: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  typeChip: {
    height: 18,
    fontSize: '0.6rem',
    marginLeft: theme.spacing(1),
  },
  tagChip: {
    height: 16,
    fontSize: '0.55rem',
  },
  tagsRow: {
    display: 'flex',
    gap: 3,
    marginTop: 3,
    flexWrap: 'wrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

function getSyncColor(status?: string): string {
  if (status === 'Synced') return '#4caf50';
  if (status === 'OutOfSync') return '#ff9800';
  return '#9e9e9e';
}

function getHealthColor(status?: string): string {
  if (status === 'Healthy') return '#4caf50';
  if (status === 'Degraded' || status === 'Missing') return '#f44336';
  if (status === 'Progressing') return '#2196f3';
  return '#9e9e9e';
}

export const ProjectDashboardCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');
  const systemName = entity.metadata.name;

  const [activeTab, setActiveTab] = useState(0);

  // ── Environments ──
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  // ── Components of this system ──
  const [components, setComponents] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    catalogApi
      .getEntities({ filter: { 'spec.system': systemName, kind: 'Component' } })
      .then(result => {
        const sorted = result.items.sort((a, b) =>
          a.metadata.name.localeCompare(b.metadata.name),
        );
        setComponents(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [catalogApi, systemName]);

  // ── ArgoCD statuses: keyed by "componentName-envKey" ──
  const [argoStatuses, setArgoStatuses] = useState<
    Record<string, ArgoStatus | null | undefined>
  >({});

  useEffect(() => {
    if (!envs.length || !components.length) return;
    components.forEach(comp => {
      const prefix =
        comp.metadata.annotations?.['fondative.io/argocd-app-prefix'];
      if (!prefix) return;
      // K8s deployment name to match image (from kubernetes-id annotation)
      const k8sId =
        comp.metadata.annotations?.['backstage.io/kubernetes-id'] ?? comp.metadata.name;
      envs.forEach(env => {
        const cacheKey = `${comp.metadata.name}-${env.key}`;
        const appName = `${prefix}-${env.key}`;
        setArgoStatuses(prev => ({ ...prev, [cacheKey]: undefined }));

        // Fetch app status + managed-resources in parallel
        const statusP = fetchApi
          .fetch(`${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}`)
          .then(res => (res.ok ? res.json() : Promise.reject()));
        const resourcesP = fetchApi
          .fetch(`${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}/managed-resources`)
          .then(res => (res.ok ? res.json() : null))
          .catch(() => null);

        Promise.all([statusP, resourcesP])
          .then(([data, resources]) => {
            // Extract image tag from first matching Deployment
            let imageTag: string | undefined;
            // Build match keywords from k8s-id: "tt-plan-frontend" → ["frontend", "ttplan"]
            const k8sName = (k8sId.split('/').pop() ?? '').toLowerCase();
            const k8sWords = k8sName.split('-').filter(w => w.length > 2);
            if (resources?.items) {
              let fallbackTag: string | undefined;
              for (const r of resources.items) {
                if (r.kind !== 'Deployment') continue;
                try {
                  const live = typeof r.liveState === 'string' ? JSON.parse(r.liveState) : r.liveState;
                  const containers = live?.spec?.template?.spec?.containers ?? [];
                  for (const c of containers) {
                    const img: string = c.image ?? '';
                    if (!img.includes(':') || img.endsWith(':latest')) continue;
                    // Only registry images
                    if (!img.includes('registry') && !img.includes('.')) continue;
                    const tag = img.split(':').pop()!;
                    const depName = (r.name ?? '').toLowerCase();
                    const imgLower = img.toLowerCase();
                    // Match: deployment name or image path contains key words from k8s-id
                    const matched = k8sWords.some(w => depName.includes(w) || imgLower.includes(w));
                    if (matched) {
                      imageTag = tag;
                      break;
                    }
                    if (!fallbackTag) fallbackTag = tag;
                  }
                } catch { /* skip parse errors */ }
                if (imageTag) break;
              }
              if (!imageTag) imageTag = fallbackTag;
            }

            setArgoStatuses(prev => ({
              ...prev,
              [cacheKey]: {
                syncStatus: data.status?.sync?.status,
                healthStatus: data.status?.health?.status,
                revision: (data.status?.sync?.revision ?? data.status?.sync?.revisions?.[0])?.substring(0, 8),
                imageTag,
              },
            }));
          })
          .catch(() =>
            setArgoStatuses(prev => ({ ...prev, [cacheKey]: null })),
          );
      });
    });
  }, [envs, components, backendBaseUrl, fetchApi]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!components.length || !envs.length) return null;

  const currentEnv = envs[activeTab];

  return (
    <Card>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {envs.map(env => (
          <Tab
            key={env.key}
            label={env.displayName}
            style={{ color: env.color }}
          />
        ))}
      </Tabs>
      <CardContent style={{ padding: 0 }}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '40%' }}>Service</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map(comp => {
              const prefix =
                comp.metadata.annotations?.['fondative.io/argocd-app-prefix'];
              const type = (comp.spec as any)?.type;
              const language =
                comp.metadata.annotations?.['fondative.io/language'];
              const tags = comp.metadata.tags ?? [];
              const cacheKey = `${comp.metadata.name}-${currentEnv.key}`;
              const argo = prefix ? argoStatuses[cacheKey] : null;

              return (
                <TableRow key={comp.metadata.name} hover>
                  {/* Service name */}
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Link
                        to={`/catalog/default/${comp.kind.toLowerCase()}/${comp.metadata.name}`}
                      >
                        <span className={classes.serviceLink}>
                          {comp.metadata.name}
                        </span>
                      </Link>
                      {type && (
                        <Chip
                          size="small"
                          label={type}
                          className={classes.typeChip}
                          variant="outlined"
                        />
                      )}
                      {language && (
                        <Chip
                          size="small"
                          label={language}
                          className={classes.typeChip}
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                    {comp.metadata.description && (
                      <Typography className={classes.meta}>
                        {comp.metadata.description}
                      </Typography>
                    )}
                    {tags.length > 0 && (
                      <Box className={classes.tagsRow}>
                        {tags.map(tag => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            className={classes.tagChip}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    {!prefix ? (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    ) : argo === undefined ? (
                      <CircularProgress size={14} />
                    ) : argo === null ? (
                      <Typography variant="caption" color="textSecondary">
                        N/A
                      </Typography>
                    ) : (
                      <Box className={classes.statusBox}>
                        <Chip
                          size="small"
                          label={argo.syncStatus ?? '?'}
                          className={classes.chip}
                          style={{
                            backgroundColor: getSyncColor(argo.syncStatus),
                          }}
                        />
                        <Chip
                          size="small"
                          label={argo.healthStatus ?? '?'}
                          className={classes.chip}
                          style={{
                            backgroundColor: getHealthColor(
                              argo.healthStatus,
                            ),
                          }}
                        />
                      </Box>
                    )}
                  </TableCell>

                  {/* Version */}
                  <TableCell>
                    {argo?.imageTag ? (
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <Typography className={classes.revision}>
                          {argo.imageTag}
                        </Typography>
                        <Tooltip title="Commit ID - Pipeline ID" arrow>
                          <InfoOutlinedIcon style={{ fontSize: 14, color: '#9e9e9e', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
